import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FetchFrequency, SourceType } from '@mg/shared';
import type { CreateSourceDto, Source } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { FormField, Input, Select, Textarea } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { fetchFrequencyLabel, sourceTypeLabel } from '../../lib/display';
import { RadioRows } from '../design/RadioRows';
import { APIFY_BACKED } from './apifySources';
import { ApifyKeyWarning } from './ApifyKeyWarning';

/**
 * Source types offered when ADDING a source.
 *
 * Newsletter is retired — an RSS feed serves the same purpose — and so is Reddit: it is an Apify
 * actor underneath (`automation-lab/reddit-scraper`), configured with the same actor id and JSON
 * input as any other, so offering it separately made one thing look like two.
 *
 * Both stay in the enum because existing rows still carry them, and a source that already IS one
 * keeps the option while being edited. Dropping a type from the list outright would make the Select
 * fall back to its first option and silently retype the source on save.
 */
const RETIRED_TYPES: ReadonlySet<SourceType> = new Set([
  SourceType.NEWSLETTER,
  SourceType.REDDIT,
]);

interface Props {
  open: boolean;
  onClose: () => void;
  tenantKey: string;
  /** Provided when editing; omit to create. */
  source?: Source;
}

export function SourceFormModal({ open, onClose, tenantKey, source }: Props) {
  const queryClient = useQueryClient();
  const editing = Boolean(source);

  const [name, setName] = useState(source?.name ?? '');
  const [type, setType] = useState<SourceType>(source?.type ?? SourceType.RSS);
  const [locator, setLocator] = useState(source?.locator ?? '');
  const [frequency, setFrequency] = useState<FetchFrequency>(
    source?.frequency ?? FetchFrequency.DAILY,
  );
  const [actorInput, setActorInput] = useState(() =>
    JSON.stringify(source?.filters?.input ?? {}, null, 2),
  );
  const [shared, setShared] = useState(source?.sharedWithWorkspace ?? false);
  const [formError, setFormError] = useState<string | null>(null);

  const selectableTypes = Object.values(SourceType).filter(
    (t) => !RETIRED_TYPES.has(t) || source?.type === t,
  );

  // The modal stays mounted across opens, so useState initializers only run once.
  // Re-sync every field from the target source each time the modal opens (or the
  // edit target changes) — otherwise "Edit" shows a blank create-style form.
  useEffect(() => {
    if (!open) return;
    setName(source?.name ?? '');
    setType(source?.type ?? SourceType.RSS);
    setLocator(source?.locator ?? '');
    setFrequency(source?.frequency ?? FetchFrequency.DAILY);
    setActorInput(JSON.stringify(source?.filters?.input ?? {}, null, 2));
    setShared(source?.sharedWithWorkspace ?? false);
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source?.id]);

  const apifyBacked = APIFY_BACKED.has(type);

  const nameId = useFieldId('source-name');
  const typeId = useFieldId('source-type');
  const locatorId = useFieldId('source-locator');
  const frequencyId = useFieldId('source-frequency');
  const actorInputId = useFieldId('source-actor-input');

  const mutation = useMutation({
    mutationFn: async (filters: Record<string, unknown> | undefined) => {
      const dto: CreateSourceDto = {
        name: name.trim(),
        type,
        locator: locator.trim(),
        frequency,
        ...(filters ? { filters } : {}),
        sharedWithWorkspace: shared,
      };
      return source ? api.updateSource(source.id, dto) : api.createSource(dto);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sources', tenantKey] });
      onClose();
    },
    onError: (err) => {
      setFormError(err instanceof ApiRequestError ? err.message : 'Failed to save source');
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim() || !locator.trim()) {
      setFormError('Name and locator are required.');
      return;
    }

    let filters: Record<string, unknown> | undefined;
    if (apifyBacked) {
      let parsed: unknown;
      try {
        parsed = actorInput.trim() === '' ? {} : JSON.parse(actorInput);
      } catch {
        setFormError('Actor input must be valid JSON.');
        return;
      }
      filters = { ...(source?.filters ?? {}), input: parsed };
    }

    mutation.mutate(filters);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit source' : 'Add source'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" form="source-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Add source'}
          </Button>
        </>
      }
    >
      <form id="source-form" onSubmit={submit} className="space-y-4">
        <FormField label="Name" htmlFor={nameId}>
          <Input
            id={nameId}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="TechCrunch RSS"
            autoFocus
            required
          />
        </FormField>

        <FormField label="Type" htmlFor={typeId}>
          <Select
            id={typeId}
            value={type}
            onChange={(e) => setType(e.target.value as SourceType)}
          >
            {selectableTypes.map((t) => (
              <option key={t} value={t}>
                {sourceTypeLabel[t]}
              </option>
            ))}
          </Select>
        </FormField>

        {/* A new tab, so adding the key does not throw away what is typed here. */}
        {apifyBacked && <ApifyKeyWarning newTab />}

        <FormField
          label={apifyBacked ? 'Apify actor ID' : 'Feed URL'}
          htmlFor={locatorId}
          hint={
            apifyBacked
              ? 'The Apify actor that powers this source, e.g. apify/rss-scraper.'
              : 'The URL of the RSS/Atom feed to pull.'
          }
        >
          <Input
            id={locatorId}
            value={locator}
            onChange={(e) => setLocator(e.target.value)}
            placeholder={apifyBacked ? 'apify/rss-scraper' : 'https://example.com/feed.xml'}
            required
          />
        </FormField>

        {apifyBacked && (
          <FormField
            label="Actor input (JSON)"
            htmlFor={actorInputId}
            hint="JSON passed to the Apify actor run. The Apify API key is managed in Site Integrations."
          >
            <Textarea
              id={actorInputId}
              rows={8}
              className="min-h-[10rem] font-mono"
              value={actorInput}
              onChange={(e) => setActorInput(e.target.value)}
              placeholder={'{\n  "startUrls": [{ "url": "https://example.com" }]\n}'}
              spellCheck={false}
            />
          </FormField>
        )}

        <FormField
          label="Fetch frequency"
          htmlFor={frequencyId}
          hint="How often we pull new items from this source."
        >
          <Select
            id={frequencyId}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as FetchFrequency)}
          >
            {Object.values(FetchFrequency).map((f) => (
              <option key={f} value={f}>
                {fetchFrequencyLabel[f]}
              </option>
            ))}
          </Select>
        </FormField>

        <div>
          <p className="mb-1 block text-sm font-medium text-ink-strong">Share</p>
          <RadioRows
            name="Share"
            value={shared ? 'workspace' : 'private'}
            onChange={(v) => setShared(v === 'workspace')}
            options={[
              { value: 'private', label: 'Only me' },
              { value: 'workspace', label: 'With workspace' },
            ]}
          />
          {/* Under the group, where it can wrap: a row's own hint is one truncated line. */}
          <p className="mt-1 text-xs text-ink-faint">
            Sharing lets teammates see this source and adds its items to their digests. Only you
            can change or delete it.
          </p>
        </div>

        {formError && <p className="text-sm text-red-600 dark:text-error-dark">{formError}</p>}
      </form>
    </Modal>
  );
}
